export interface ReorderMover {
  key: string;
  label: string;
  fromIndex: number;
  toIndex: number;
}

/** Items whose relative position changed between two orderings, computed as the complement of the
 * longest common subsequence over the keys shared by both lists. Keys present on only one side
 * (membership add/remove) are excluded — they are separate create/delete changes, not moves. */
export function computeReorderMovers(
  before: string[],
  after: string[],
  keyLabels: Record<string, string> = {}
): ReorderMover[] {
  const beforeIndex = new Map(before.map((k, i) => [k, i]));
  const afterIndex = new Map(after.map((k, i) => [k, i]));
  const b = before.filter((k) => afterIndex.has(k));
  const a = after.filter((k) => beforeIndex.has(k));

  // LCS of the two shared-key sequences.
  const dp: number[][] = Array.from({ length: b.length + 1 }, () => new Array(a.length + 1).fill(0));
  for (let i = b.length - 1; i >= 0; i--) {
    for (let j = a.length - 1; j >= 0; j--) {
      dp[i][j] = b[i] === a[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const stable = new Set<string>();
  let i = 0;
  let j = 0;
  while (i < b.length && j < a.length) {
    if (b[i] === a[j]) {
      stable.add(b[i]);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i++;
    } else {
      j++;
    }
  }

  const movers: ReorderMover[] = [];
  for (const key of a) {
    if (stable.has(key)) continue;
    movers.push({
      key,
      label: keyLabels[key] ?? key,
      fromIndex: beforeIndex.get(key)!,
      toIndex: afterIndex.get(key)!,
    });
  }
  // Present movers in target order (they lead the "what moved" summary).
  return movers.sort((x, y) => x.toIndex - y.toIndex);
}

export interface NewItemPlacement {
  key: string;
  label: string;
  toIndex: number;
}

/** New keys (present in `after`, absent from `before`) that the reorder places ahead of existing items, at
 * their index in `after`. A CREATE appends, so the run of new keys that ends `after` gets there without the
 * reorder and is not a placement. That is where `_natural_order_after_membership` in
 * `server/configuration_repository/diff_engine.py` puts new keys. */
export function computeNewItemPlacements(
  before: string[],
  after: string[],
  keyLabels: Record<string, string> = {}
): NewItemPlacement[] {
  const beforeKeys = new Set(before);
  let trailingNewStart = after.length;
  while (trailingNewStart > 0 && !beforeKeys.has(after[trailingNewStart - 1])) {
    trailingNewStart--;
  }
  const placements: NewItemPlacement[] = [];
  for (let toIndex = 0; toIndex < trailingNewStart; toIndex++) {
    const key = after[toIndex];
    if (!beforeKeys.has(key)) {
      placements.push({ key, label: keyLabels[key] ?? key, toIndex });
    }
  }
  return placements;
}
